/**
 * The Technology overlay (M1.1, reworked at M1.5): the two division queues —
 * Process R&D and Product Design — with funding levels, engineer staffing and
 * queue order as the levers; the IP library and the researchable variant
 * space; the design desk, whose preview is answered by the sim's own
 * `plan_design` through the probe channel; mask revisions on shipped
 * products; and the node ladder. The store already knows what is legal and
 * what the firm can pay — the overlay greys out what the sim would drop and
 * never re-derives eligibility.
 */

import { useEffect, useState } from 'react';
import { useSyncExternalStore } from 'react';
import type { DesignPreviewUi, DivisionUi, FloorHost, Funding, ProjectUi } from '../bridge';
import { money } from './format';
import { openHandbook, setPanels, usePanels, type TechnologyTab } from './panels';
import { snapshot, subscribe } from './store';
import { Tip, atomic } from './Tooltip';

const FUNDING: Funding[] = ['trickle', 'steady', 'crash'];

function ProjectRow({
  host,
  firm,
  p,
  queueLen,
  running,
}: {
  host: FloorHost;
  firm: string;
  p: ProjectUi;
  queueLen: number;
  running: boolean;
}) {
  const division = p.division as 'process' | 'design';
  return (
    <tr>
      <td>
        {p.label}
        {p.slippedWeeks > 0 && (
          <span className="chip warn" title={`${p.slippedWeeks} weeks slipped — each one still burned its instalment`}>
            +{p.slippedWeeks} slipped
          </span>
        )}
      </td>
      <td>
        {p.kind === 'license' ? (
          <span className="muted" title="a vendor transfer holds its schedule — no funding lever">
            fixed
          </span>
        ) : (
          <select
            value={p.funding}
            disabled={!running}
            title="trickle: cheap, slow, safe · steady: the baseline · crash: fast, dear, slippery"
            onChange={(e) =>
              host.sendSim({
                SetProjectFunding: { firm, division, index: p.index, funding: e.target.value as Funding },
              })
            }
          >
            {FUNDING.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        )}
      </td>
      <td title="engineers staffed of the project's ask — the queue staffs top-first">
        {p.kind === 'license' ? '—' : `${p.engineersStaffed}/${p.engineersAsk}`}
        {p.kind !== 'license' && p.engineersStaffed < p.engineersAsk && (
          <span className="chip warn" title="understaffed: progress and burn scale down together">
            starved
          </span>
        )}
      </td>
      <td>
        <span className="progress" title={`${p.progressWeeks} of ${p.durationWeeks} weeks · ~${p.etaWeeks} wk left at this pace`}>
          <span className="progress-bar" style={{ width: `${Math.min(100, p.progressMille / 10)}%` }} />
          <span className="progress-label">
            {p.progressWeeks}/{p.durationWeeks} wk
          </span>
        </span>
      </td>
      <td>
        <Tip figure={p.costFigure}>
          <span>{money(p.weeklyCost)}/wk</span>
        </Tip>
      </td>
      <td>
        {p.index > 0 && (
          <button
            className="small"
            disabled={!running}
            title="move up — the queue staffs top-first"
            onClick={() => host.sendSim({ MoveProject: { firm, division, index: p.index, to: p.index - 1 } })}
          >
            ↑
          </button>
        )}
        {p.index < queueLen - 1 && (
          <button
            className="small"
            disabled={!running}
            title="move down"
            onClick={() => host.sendSim({ MoveProject: { firm, division, index: p.index, to: p.index + 1 } })}
          >
            ↓
          </button>
        )}
        <button
          className="small"
          disabled={!running}
          title="cancel — what has been spent is lost"
          onClick={() => host.sendSim({ CancelProject: { firm, division, index: p.index } })}
        >
          ×
        </button>
      </td>
    </tr>
  );
}

function Division({
  host,
  firm,
  d,
  running,
  hint,
}: {
  host: FloorHost;
  firm: string;
  d: DivisionUi;
  running: boolean;
  hint?: string;
}) {
  const division = d.id as 'process' | 'design';
  return (
    <section>
      <h3>
        {d.name}
        {d.engineers != null && (
          <span className="muted" style={{ marginLeft: '0.6em' }}>
            <Tip figure={d.wageFigure}>
              <span>
                {d.engineers} engineers · {money(d.wageWeekly)}/wk
              </span>
            </Tip>
            <button
              className="small"
              disabled={!running || d.engineers === 0}
              title="release one engineer"
              onClick={() => host.sendSim({ SetEngineers: { firm, division, count: d.engineers! - 1 } })}
            >
              −
            </button>
            <button
              className="small"
              disabled={!running}
              title="hire one engineer — paid weekly, working or idle"
              onClick={() => host.sendSim({ SetEngineers: { firm, division, count: d.engineers! + 1 } })}
            >
              +
            </button>
            {d.demanded > (d.engineers ?? 0) && (
              <span className="chip warn" title={`the queue asks for ${d.demanded} — the tail runs understaffed`}>
                over-asked
              </span>
            )}
          </span>
        )}
      </h3>
      {hint && <div className="hint">{hint}</div>}
      {d.queue.length === 0 ? (
        <div className="hint">Nothing on the queue.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>project</th>
              <th>funding</th>
              <th title="staffed / asked">team</th>
              <th>progress</th>
              <th>burn</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {d.queue.map((p) => (
              <ProjectRow key={`${p.division}-${p.index}`} host={host} firm={firm} p={p} queueLen={d.queue.length} running={running} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** The design desk (M1.5): a generation, a node, a loadout — previewed by the sim. */
function DesignDesk({ host, firm, running }: { host: FloorHost; firm: string; running: boolean }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const c = hud.campaign;
  const tech = c?.tech;
  const [generation, setGeneration] = useState('');
  const [node, setNode] = useState('');
  const [name, setName] = useState('');
  const [loadout, setLoadout] = useState<(number | null)[]>([]);
  const [preview, setPreview] = useState<DesignPreviewUi | null>(null);

  const gen = tech?.generations.find((g) => g.id === generation) ?? null;
  const attained = tech?.nodes.filter((n) => n.attained) ?? [];
  const nodeUi = tech?.nodes.find((n) => n.id === node) ?? null;

  // Keep the form shaped like the chosen generation.
  useEffect(() => {
    if (!tech) return;
    const first = tech.generations[0];
    if (!gen && first) {
      setGeneration(first.id);
      return;
    }
    if (gen && loadout.length !== gen.slots.length) {
      setLoadout(gen.slots.map(() => null));
    }
    const deepest = attained[attained.length - 1];
    if (!node && deepest) {
      setNode(deepest.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tech, generation, node, gen?.slots.length]);

  // The preview: the sim's own plan_design, asked at the form's cadence.
  useEffect(() => {
    if (!gen || !node || loadout.length !== gen.slots.length) return;
    let live = true;
    host.designPreview({ generation: gen.id, node, loadout }).then((p) => {
      if (live) setPreview(p);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation, node, JSON.stringify(loadout), c?.week]);

  if (!tech || tech.generations.length === 0) return null;
  const id = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const family = nodeUi?.family ?? '';
  const candidates = (block: string) => tech.library.filter((e) => e.block === block && e.family === family);
  const startable = running && preview?.ok === true && id.length > 0;
  return (
    <section>
      <h3>
        Design desk
        <button className="help" title="the handbook: blocks and loadouts" onClick={() => openHandbook('blocks')}>
          ?
        </button>
      </h3>
      <div className="design-desk">
        <div className="design-form">
          <label>
            generation{' '}
            <select value={generation} onChange={(e) => setGeneration(e.target.value)}>
              {tech.generations.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.categoryName})
                </option>
              ))}
            </select>
          </label>
          <label>
            node{' '}
            <select value={node} onChange={(e) => setNode(e.target.value)}>
              {attained.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name} ({n.family})
                </option>
              ))}
            </select>
          </label>
          {gen?.slots.map((slot, i) => (
            <label key={slot.id}>
              {slot.name}
              {!slot.required && <span className="muted"> (optional)</span>}{' '}
              <select
                value={loadout[i] ?? ''}
                onChange={(e) => {
                  const next = [...loadout];
                  next[i] = e.target.value === '' ? null : Number(e.target.value);
                  setLoadout(next);
                }}
              >
                <option value="">— empty —</option>
                {candidates(slot.block).map((e) => (
                  <option key={e.index} value={e.index}>
                    {e.name}
                    {e.proven ? ' ✓' : ''}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <label>
            name <input type="text" value={name} placeholder="MD-8" onChange={(e) => setName(e.target.value)} />
          </label>
          <button
            disabled={!startable}
            title={
              preview?.ok === false
                ? (preview.refusal ?? 'the desk refuses this brief')
                : id.length === 0
                  ? 'name the design'
                  : `to the queue: ${preview ? `${preview.durationWeeks} wk, ${money(preview.cost)}` : ''}`
            }
            onClick={() =>
              gen &&
              host.sendSim({
                StartDesign: {
                  firm,
                  id,
                  name: name.trim(),
                  generation: gen.id,
                  node,
                  loadout,
                  funding: 'steady',
                },
              })
            }
          >
            Engineer to tape-out
          </button>
        </div>
        <div className="design-preview">
          {preview == null ? (
            <div className="hint">Pick a loadout — the desk prices it.</div>
          ) : preview.ok ? (
            <>
              <div>
                <Tip figure={preview.dieFigure}>
                  <span>
                    die <strong>{preview.dieMm2} mm²</strong>
                  </span>
                </Tip>
                {' · '}
                <span title="the design's timing ceiling, per-mille of the category baseline">
                  clock <strong>{(preview.perfMille / 1000).toFixed(2)}×</strong>
                </span>
                {' · '}
                <Tip figure={preview.effortFigure}>
                  <span>
                    {preview.durationWeeks} wk · {money(preview.cost)} · {preview.engineers} engineers
                  </span>
                </Tip>
              </div>
              <div className="design-attrs">
                {preview.attrs.map((a) => (
                  <Tip key={a.id} figure={a.figure}>
                    <span className={a.valueMille <= 100 ? 'muted' : ''}>
                      {a.name}: <strong>{a.valueMille}‰</strong>
                    </span>
                  </Tip>
                ))}
              </div>
            </>
          ) : (
            <div className="hint warn">{preview.refusal}</div>
          )}
        </div>
      </div>
    </section>
  );
}

export function Technology({ host }: { host: FloorHost }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const panels = usePanels();
  const [portPick, setPortPick] = useState<Record<number, string>>({});
  const [variantFamily, setVariantFamily] = useState('');
  const c = hud.campaign;
  if (panels.economy !== 'technology' || !c) return null;
  const firm = c.firm;
  const tech = c.tech;
  const running = c.run === 'running';
  const busy = tech.process.queue.some((p) => p.kind === 'research' || p.kind === 'license');
  const capabilityFamily = tech.nodes.find((n) => n.id === firm.capability)?.family ?? tech.families[0] ?? '';
  const familyPick = variantFamily || capabilityFamily;
  const revisable = firm.products.filter((p) => p.wafersRun > 0 || p.revisions > 0);
  const tab = panels.technologyTab;
  const tabs: [TechnologyTab, string, string][] = [
    ['process', 'Process Technology', 'the node ladder and the Process R&D queue'],
    ['components', 'Components', 'the IP library and the researchable block variants'],
    ['designs', 'Chip Designs', 'the design desk and mask revisions'],
  ];
  const poolHint =
    'One Product Design pool serves components and chip designs alike — the queue staffs top-first across both.';
  return (
    <div className="overlay technology">
      <header>
        <h2>Technology</h2>
        <span className="muted">
          industry frontier <strong>{tech.frontierName}</strong> · your capability <strong>{firm.capability ?? '—'}</strong> ·{' '}
          {tech.completedProjects} projects completed
        </span>
        <button className="help" title="the handbook: the research pipeline" onClick={() => openHandbook('research')}>
          ?
        </button>
        <button className="close" title="close" onClick={() => setPanels({ economy: null })}>
          ×
        </button>
      </header>

      <nav className="overlay-tabs">
        {tabs.map(([id, label, title]) => (
          <button
            key={id}
            className={tab === id ? 'active' : ''}
            title={title}
            onClick={() => setPanels({ technologyTab: id })}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'process' && <Division host={host} firm={firm.id} d={tech.process} running={running} />}
      {/* The one Product Design pool serves both of the tabs that feed it. */}
      {tab !== 'process' && (
        <Division host={host} firm={firm.id} d={tech.design} running={running} hint={poolHint} />
      )}
      {tab === 'designs' && <DesignDesk host={host} firm={firm.id} running={running} />}

      {tab === 'components' && (
        <>
      <section>
        <h3>IP library</h3>
        {tech.library.length === 0 ? (
          <div className="hint">Nothing developed yet — point a Product Design project at a variant below.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>variant</th>
                <th>tier</th>
                <th>style</th>
                <th>family</th>
                <th title="carried by a design that shipped wafers — proven entries discount the next design">proven</th>
                <th>port</th>
              </tr>
            </thead>
            <tbody>
              {tech.library.map((e) => (
                <tr key={e.index}>
                  <td>{e.name}</td>
                  <td>{e.tier}</td>
                  <td>{e.style}</td>
                  <td>{e.family}</td>
                  <td>{e.proven ? <span className="chip ok">✓</span> : <span className="muted">not yet</span>}</td>
                  <td>
                    {e.portFamilies.length > 0 && (
                      <>
                        <select
                          value={portPick[e.index] ?? e.portFamilies[0] ?? ''}
                          onChange={(ev) => setPortPick({ ...portPick, [e.index]: ev.target.value })}
                        >
                          {e.portFamilies.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                        <button
                          className="small"
                          disabled={!running}
                          title={`port: ${money(e.portCost)} over ${e.portWeeks} wk — cheaper than redeveloping`}
                          onClick={() =>
                            host.sendSim({
                              StartPort: {
                                firm: firm.id,
                                entry: e.index,
                                family: portPick[e.index] ?? e.portFamilies[0] ?? '',
                                funding: 'steady',
                              },
                            })
                          }
                        >
                          Port
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3>
          Researchable variants
          <span className="muted" style={{ marginLeft: '0.6em' }}>
            develop for{' '}
            <select value={familyPick} onChange={(e) => setVariantFamily(e.target.value)}>
              {tech.families.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </span>
        </h3>
        <table>
          <thead>
            <tr>
              <th>variant</th>
              <th>block</th>
              <th>tier</th>
              <th>style</th>
              <th title="area in thousands of cells — silicon falls out of the node">kcells</th>
              <th>development</th>
              <th>team</th>
              <th title="chance a development week slips">slip</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tech.templates.map((t) => {
              const owned = t.ownedFamilies.includes(familyPick);
              return (
                <tr key={t.id}>
                  <td>
                    <Tip figure={t.contribFigure}>
                      <span>{t.name}</span>
                    </Tip>
                  </td>
                  <td>{t.block}</td>
                  <td>{t.tier}</td>
                  <td>{t.style}</td>
                  <td>{t.kcells}k</td>
                  <td>
                    {money(t.developCost)} · {t.developWeeks} wk
                  </td>
                  <td>{t.engineers}</td>
                  <td>{(t.slipPerMille / 10).toFixed(0)}%</td>
                  <td>
                    {owned ? (
                      <span className="chip ok" title={`in the library for ${familyPick} (or on the queue)`}>
                        owned
                      </span>
                    ) : (
                      <button
                        className="small"
                        disabled={!running}
                        title={`develop for the ${familyPick} family: ${money(t.developCost)} over ${t.developWeeks} wk`}
                        onClick={() =>
                          host.sendSim({
                            StartVariant: { firm: firm.id, template: t.id, family: familyPick, funding: 'steady' },
                          })
                        }
                      >
                        Develop
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

        </>
      )}

      {tab === 'designs' && revisable.length > 0 && (
        <section>
          <h3>Mask revisions</h3>
          <table>
            <thead>
              <tr>
                <th>product</th>
                <th title="cumulative wafers run — a revision is learning from the line">wafers run</th>
                <th>revisions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {revisable.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.wafersRun}</td>
                  <td>
                    <Tip figure={p.revisionFigure}>
                      <span>{p.revisions}</span>
                    </Tip>
                  </td>
                  <td>
                    <button
                      className="small"
                      disabled={!p.canRevise || !running}
                      title={
                        p.canRevise
                          ? `revise: ${money(p.revisionCost)} over ${p.revisionWeeks} wk — diminishing returns per revision`
                          : 'needs shipped volume behind it (and one revision at a time)'
                      }
                      onClick={() =>
                        host.sendSim({ StartMaskRevision: { firm: firm.id, product: p.id, funding: 'steady' } })
                      }
                    >
                      Revise masks
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {tab === 'process' && (
      <section>
        <h3>The ladder</h3>
        <table>
          <thead>
            <tr>
              <th>node</th>
              <th>family</th>
              <th>feature</th>
              <th>wafer</th>
              <th>R&amp;D</th>
              <th title="engineers a node project asks of the Process pool">team</th>
              <th title="chance a research week slips">slip</th>
              <th>license</th>
              <th>fab block</th>
              <th>block opex/wk</th>
              <th>wafers/wk</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tech.nodes.map((n) => (
              <tr key={n.id} className={n.attained ? 'me' : ''}>
                <td>
                  {n.name}
                  {n.attained && (
                    <span className="chip ok" title="attained — you can build to it">
                      ✓
                    </span>
                  )}
                  {n.isFrontier && (
                    <span className="chip warn" title="the industry frontier">
                      ★ frontier
                    </span>
                  )}
                  {n.isResearchTarget && !n.attained && (
                    <span className="chip" title="the next rung for you">
                      next
                    </span>
                  )}
                </td>
                <td>{n.family}</td>
                <td>{n.feature}</td>
                <td>{n.waferInches}″</td>
                <td>
                  <Tip
                    figure={atomic(
                      'R&D',
                      `${money(n.rndCost)} over ${n.rndWeeks} wk`,
                      `Paid as ${money(n.rndWeekly)} a week at steady funding while the project runs; crash funding trades money and slip risk for calendar time.`,
                    )}
                  >
                    {money(n.rndCost)} · {n.rndWeeks} wk
                  </Tip>
                </td>
                <td>{n.rndEngineers}</td>
                <td>{(n.rndSlipPerMille / 10).toFixed(0)}%</td>
                <td>
                  {money(n.licenseCost)} · {n.licenseWeeks} wk
                </td>
                <td>
                  {money(n.fabCapex)} · {n.fabBuildWeeks} wk
                </td>
                <td>{money(n.fabWeeklyOpex)}</td>
                <td>{n.wafersPerWeek}</td>
                <td>
                  {n.isResearchTarget && (
                    <button
                      className="small"
                      disabled={busy || !running}
                      title={busy ? 'one node project at a time' : `queue R&D: ${money(n.rndCost)} over ${n.rndWeeks} wk (steady — re-fund it on the queue)`}
                      onClick={() => host.sendSim({ StartResearch: { firm: firm.id, node: n.id, funding: 'steady' } })}
                    >
                      Research
                    </button>
                  )}
                  {n.licensable && (
                    <button
                      className="small"
                      disabled={!n.canAffordLicense || busy || !running}
                      title={
                        busy
                          ? 'one node project at a time'
                          : n.canAffordLicense
                            ? `license: ${money(n.licenseCost)} now, ${n.licenseWeeks} wk transfer`
                            : `cannot afford ${money(n.licenseCost)}`
                      }
                      onClick={() => host.sendSim({ LicenseNode: { firm: firm.id, node: n.id } })}
                    >
                      License
                    </button>
                  )}
                </td>
                <td>
                  {n.canBuildFab && (
                    <button
                      className="small"
                      disabled={!n.canAffordFab || !running}
                      title={n.canAffordFab ? `build a block: ${money(n.fabCapex)}, ${n.fabBuildWeeks} wk` : `cannot afford ${money(n.fabCapex)}`}
                      onClick={() => host.sendSim({ BuildFab: { firm: firm.id, node: n.id } })}
                    >
                      Build fab block
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      )}
    </div>
  );
}
